// FAQ accordion open/close animation (inventory 3.7). Native
// <details>/<summary> stays keyboard- and screen-reader-native, but
// the browser's own toggling of the [open] attribute is not purely
// CSS-driven in current engines (a CSS Grid 0fr/1fr row-track
// override of the native show/hide was tried first and dropped: the
// transition fired inconsistently in both directions, worse on
// close). This script takes over the whole open/close lifecycle
// instead (preventDefault on the summary click, then it alone flips
// details.open), animating the <details> element's own height via the
// Web Animations API so there is no race with native toggle timing.
// fill: 'forwards' keeps the animated height applied through to the
// exact tick the inline height is cleared in onfinish, so the box
// never flashes back to its unanimated (auto) height between the
// animation ending and the open/close state actually flipping.
// Respects prefers-reduced-motion (native instant toggle stays).
// Multi-instance via any <details> inside .th-faq that has a
// .th-faq__panel wrapper around the answer. React twin:
// components/faq/Faq.tsx (Storybook renders the static markup only,
// no script attached).
// getComputedStyle serializes CSS <time> custom properties in seconds
// (e.g. "200ms" comes back as ".2s"), never milliseconds; parseFloat
// alone silently drops the unit and reads ".2s" as 0.2, so a WAAPI
// duration ends up 1000x too short (0.2ms, an unnoticeable snap
// instead of a 200ms slide). Only a value actually still carrying an
// "ms" suffix should be read as-is; everything else is seconds.
function parseCssTimeToMs(value: string, fallbackMs: number): number {
  const trimmed = value.trim();
  const num = parseFloat(trimmed);
  if (Number.isNaN(num)) {
    return fallbackMs;
  }
  return trimmed.endsWith('ms') ? num : num * 1000;
}

function initFaqAccordion(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const rootStyle = getComputedStyle(document.documentElement);
  const duration = parseCssTimeToMs(rootStyle.getPropertyValue('--th-dur-base'), 200);
  const easing = rootStyle.getPropertyValue('--th-ease').trim() || 'ease';

  document.querySelectorAll<HTMLDetailsElement>('.th-faq details').forEach((details) => {
    const summary = details.querySelector<HTMLElement>('summary');
    const panel = details.querySelector<HTMLElement>('.th-faq__panel');
    if (!summary || !panel) {
      return;
    }

    let animation: Animation | null = null;

    function animateHeight(endHeight: number, onDone: () => void): void {
      const startHeight = details.getBoundingClientRect().height;
      animation?.cancel();
      animation = details.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration, easing, fill: 'forwards' },
      );
      animation.onfinish = () => {
        animation = null;
        onDone();
      };
    }

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      details.style.overflow = 'hidden';

      if (!details.open) {
        // [open] must be set before the panel can be measured, but the
        // box is pinned right back to its closed height in the same
        // tick so the reveal never flashes full-size before the
        // animation to the real open height starts on the next frame.
        const closedHeight = details.getBoundingClientRect().height;
        details.open = true;
        details.style.height = `${closedHeight}px`;
        requestAnimationFrame(() => {
          const openHeight = summary.getBoundingClientRect().height + panel.getBoundingClientRect().height;
          animateHeight(openHeight, () => {
            details.style.height = '';
            details.style.overflow = '';
          });
        });
      } else {
        const closedHeight = summary.getBoundingClientRect().height;
        animateHeight(closedHeight, () => {
          details.open = false;
          details.style.height = '';
          details.style.overflow = '';
        });
      }
    });
  });
}
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initFaqAccordion) : initFaqAccordion();
